/**
 * Views/HouseTreasury/HouseTreasury.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * House Treasury module shell (requirement §8, §75, AC-01, AC-02).
 *
 * Seven tabs: Dashboard, Game Owner Performance, Games, Treasury Controls,
 * Complete Ledger, Daily Report, Audit Logs.
 *
 * This module is a monitoring, accounting, ledger, reconciliation, reporting and
 * audit surface. It contains no Add VC, Redeem VC, Transfer VC or Settle VC
 * anywhere (§7, AC-03) — those operations stay in the existing business modules,
 * and having a second place to perform them is precisely what the requirement
 * forbids.
 *
 * Access is gated on the Super-User role, both here and independently on every
 * backend endpoint. The frontend gate is convenience; the backend gate is the
 * control.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import * as React from "react";
import { Box, Tab, Tabs, Typography, Chip, Alert } from "@mui/material";
import { useGetIdentity } from "react-admin";

import DashboardIcon from "@mui/icons-material/SpaceDashboard";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import CasinoIcon from "@mui/icons-material/Casino";
import TuneIcon from "@mui/icons-material/Tune";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SummarizeIcon from "@mui/icons-material/Summarize";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";

import { DashboardTab } from "./tabs/DashboardTab";
import { GameOwnerTab } from "./tabs/GameOwnerTab";
import { GamesTab } from "./tabs/GamesTab";
import { ControlsTab } from "./tabs/ControlsTab";
import { LedgerTab } from "./tabs/LedgerTab";
import { ReportsTab } from "./tabs/ReportsTab";
import { AuditTab } from "./tabs/AuditTab";
import { StatusChip, GOLD, BORDER } from "./components/common";
import { getTreasuryStatus } from "./api";

const TABS = [
  { key: "dashboard", label: "Dashboard", icon: <DashboardIcon sx={{ fontSize: 17 }} /> },
  { key: "gameOwners", label: "Game Owner Performance", icon: <AccountTreeIcon sx={{ fontSize: 17 }} /> },
  { key: "games", label: "Games", icon: <CasinoIcon sx={{ fontSize: 17 }} /> },
  { key: "controls", label: "Treasury Controls", icon: <TuneIcon sx={{ fontSize: 17 }} /> },
  { key: "ledger", label: "Complete Ledger", icon: <ReceiptLongIcon sx={{ fontSize: 17 }} /> },
  { key: "reports", label: "Daily Report", icon: <SummarizeIcon sx={{ fontSize: 17 }} /> },
  { key: "audit", label: "Audit Logs", icon: <HistoryEduIcon sx={{ fontSize: 17 }} /> },
];

export const HouseTreasury = () => {
  const { data: identity } = useGetIdentity();
  const [tab, setTab] = React.useState(0);
  const [status, setStatus] = React.useState(null);
  const [accessError, setAccessError] = React.useState("");

  // One filter object shared across tabs so switching tabs keeps the period the
  // operator was looking at.
  const [filters, setFilters] = React.useState({
    preset: "last7",
    startDate: "",
    endDate: "",
  });

  const refreshStatus = React.useCallback(async () => {
    try {
      const result = await getTreasuryStatus();
      setStatus(result);
      setAccessError("");
    } catch (err) {
      setAccessError(err.message);
    }
  }, []);

  React.useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  const isSuperUser = identity?.userRoleName === "Super-User";

  if (identity && !isSuperUser) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          House Treasury is restricted to Super Admin.
        </Alert>
      </Box>
    );
  }

  const tabProps = { filters, onFiltersChange: setFilters };

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 2.5 },
        width: "100%",
        maxWidth: "100%",
        // react-admin's content area is a flex child, which defaults to
        // min-width:auto and so refuses to shrink below its content width.
        // Without this a wide table stretches the whole document and produces
        // a page-level horizontal scrollbar.
        minWidth: 0,
        overflowX: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography sx={{ color: "#FFF", fontSize: "1.25rem", fontWeight: 800 }}>
            House Treasury
          </Typography>
          <Typography sx={{ color: "#9E9E9E", fontSize: "0.72rem" }}>
            Centralized VC accounting, ledger, reconciliation and audit for Vegas Empire
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {status?.criticalExceptions > 0 && (
            <Chip
              size="small"
              label={`${status.criticalExceptions} critical reconciliation exception${
                status.criticalExceptions === 1 ? "" : "s"
              }`}
              sx={{ backgroundColor: "#4E1C1C", color: "#EF9A9A", fontSize: "0.68rem" }}
            />
          )}
          {status && <StatusChip value={status.status} />}
        </Box>
      </Box>

      {accessError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {accessError}
        </Alert>
      )}

      {status?.status === "FROZEN" && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Treasury is frozen.</strong> Recharge and redeem are blocked platform-wide.
          {status.freezeReason ? ` Reason: ${status.freezeReason}` : ""} Gameplay and bonuses are
          unaffected.
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          borderBottom: `1px solid ${BORDER}`,
          mb: 2.5,
          "& .MuiTab-root": {
            color: "#9E9E9E",
            textTransform: "none",
            fontSize: "0.78rem",
            minHeight: 44,
          },
          "& .Mui-selected": { color: `${GOLD} !important` },
          "& .MuiTabs-indicator": { backgroundColor: GOLD },
        }}
      >
        {TABS.map((t) => (
          <Tab key={t.key} label={t.label} icon={t.icon} iconPosition="start" />
        ))}
      </Tabs>

      {/* Tabs are mounted on demand: the ledger and audit tabs issue heavy
          queries, and mounting them all up front would fire every one on load. */}
      {tab === 0 && <DashboardTab {...tabProps} />}
      {tab === 1 && <GameOwnerTab {...tabProps} />}
      {tab === 2 && <GamesTab {...tabProps} />}
      {tab === 3 && <ControlsTab onStatusChange={refreshStatus} />}
      {tab === 4 && <LedgerTab {...tabProps} />}
      {tab === 5 && <ReportsTab />}
      {tab === 6 && <AuditTab {...tabProps} />}
    </Box>
  );
};

export default HouseTreasury;
